import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";


export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/products/`,
    prepareHeaders: (headers, { getState }) => {
      const cookies = document.cookie.split(";");
      let jwtCookie = null;

      cookies.forEach((cookie) => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith("token=")) {
          jwtCookie = "Bearer " + trimmedCookie.split("token=")[1];
        }
      });

      if (jwtCookie) {
        headers.set("authorization", jwtCookie);
      }

      return headers;
    },
  }),
  tagTypes: ['ProductFavourites', 'FavouriteProducts'],
  endpoints: (builder) => ({

    createProduct: builder.mutation({
      query: (body) => ({
        url: `create-product`,
        method: "POST",
        body: body
      }),
    }),

    updateProduct: builder.mutation({
      query: ({ productId, ...body }) => ({
        url: `edit-product/${productId}`,
        method: "POST",
        body: body
      }),
    }),

    addProductFavourite: builder.mutation({
      query: (body) => ({
        url: `add-product-favouritelist`,
        method: "POST",
        body: body
      }),
      invalidatesTags: ['ProductFavourites', 'FavouriteProducts'],
    }),

    updateProductFavourite: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `update-product-favouritelist/${id}`,
        method: "PUT",
        body: body
      }),
      invalidatesTags: ['ProductFavourites'],
    }),

    productslist: builder.query({
      query: () => ({
        url: `productslist`,
        method: "GET"
      }),
    }),

    getDashboardMetrics: builder.query({
      query: () => ({
        url: `dashboard-metrics`,
        method: "GET"
      }),
    }),

    // Shared "Just Released" / "Recently Approved" feed — FDA novel
    // approvals from the recent six-month boundary window, sorted by
    // approval date DESC. Supports page-based infinite scrolling.
    getJustReleased: builder.query({
      query: ({ limit = 8, page = 1 } = {}) => ({
        url: `just-released?limit=${limit}&page=${page}`,
        method: "GET",
      }),
    }),

    removeProductFavourite: builder.mutation({
      query: (body) => ({
        url: `remove-product-favouritelist`,
        method: "DELETE",
        body: body
      }),
      invalidatesTags: ['ProductFavourites', 'FavouriteProducts'],
    }),

    searchProductsByIds: builder.mutation({
      query: (body) => ({
        url: `search-products-by-ids`,
        method: "POST",
        body: body
      }),
    }),

    compareProducts: builder.mutation({
      query: (products) => ({
        url: `compare`,
        method: "POST",
        body: { products },
      }),
    }),

    searchProductsByRepsId: builder.query({
      query: (id) => ({
        url: `search-products-by-reps-id/${id}`,
        method: "GET",
      }),
    }),

    favouriteProductList: builder.query({
      query: () => ({
        url: `user-product-favouritelist`,
        method: "GET",
      }),
      providesTags: ['ProductFavourites'],
    }),

    getFavouriteProducts: builder.query({
      query: (id) => ({
        url: `fav-product-list?id=${id}`,
        method: "GET",
      }),
      providesTags: ['FavouriteProducts'],
    }),

    searchList: builder.query({
      query: ({ search, page, type, searchQuery, searchTerm }) => {
        const params = new URLSearchParams();

        if (search !== undefined && search !== null && search !== "") {
          params.set("search", String(search));
        }
        if (type !== undefined && type !== null) {
          params.set("type", String(type));
        }
        if (page !== undefined && page !== null) {
          params.set("page", String(page));
        }
        if (searchQuery !== undefined && searchQuery !== null) {
          params.set("searchQuery", String(searchQuery));
        }
        if (searchTerm !== undefined && searchTerm !== null) {
          params.set("searchTerm", String(searchTerm));
        }

        return {
          url: `searchlist?${params.toString()}`,
          method: "GET",
        }
      },
    }),

    searchListForProductAdd: builder.query({
      query: ({ search, page }) => {
        const params = new URLSearchParams({ page: String(page || 1) });
        if (search) params.set("search", search);
        return {
          url: `searchlist-for-product-add?${params.toString()}`,
          method: "GET",
        }
      },
    }),

    termList: builder.query({
      query: ({ search, page, type, loggedIn=false, ip, afterKey }) => {
        let url = `publicsearchterms?${search ? `search=${search}&` : ''}type=${type}&page=${page}${ip ? `&ip=${ip}` : ''}`;

        if (afterKey) {
          url += `&afterKey=${encodeURIComponent(afterKey)}`;
        }

        return {
          url: url,
          method: "GET",
        }
      },
    }),
  }),
})



export const {
  useCreateProductMutation,
  useGetDashboardMetricsQuery,
  useGetJustReleasedQuery,
  useUpdateProductMutation,
  useAddProductFavouriteMutation,
  useUpdateProductFavouriteMutation,
  useFavouriteProductListQuery,
  useRemoveProductFavouriteMutation,
  useProductslistQuery,
  useGetFavouriteProductsQuery,
  useSearchListQuery,
  useSearchListForProductAddQuery,
  useTermListQuery,
  useSearchProductsByIdsMutation,
  useCompareProductsMutation,
  useSearchProductsByRepsIdQuery

} = productsApi
